import React, { useState } from 'react';
import { useResume } from '../context/ResumeContext';
import { normaliseCategories, uid, SUGGESTED_CATEGORIES } from './skills/skillsUtils';
import CategoryCard from './skills/CategoryCard';
import AddCategoryPanel from './skills/AddCategoryPanel';

/**
 * SkillsSection — form editor for the Skills section.
 *
 * Two modes:
 *  1. FLAT MODE   — no categories set up yet. Shows all skills as tags.
 *                   User can add/remove skills and optionally enable categories.
 *  2. CATEGORY MODE — user has defined at least one category.
 *                   Shows a CategoryCard per category. Skills added to a
 *                   category are ONLY in that category (not duplicated flat).
 *
 * Data rules:
 *  - In FLAT MODE: resume.skills = [...], resume.skillCategories = []
 *  - In CATEGORY MODE: resume.skillCategories = [{id,name,skills},...],
 *                      resume.skills = flat union of all category skills (for ATS renderers)
 *  - NEVER wrap flat skills into a fake category — that corrupts the data model.
 */
export default function SkillsSection() {
  const { activeResume, updateResumeState } = useResume();
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [flatInput, setFlatInput] = useState('');

  const rawSkills = Array.isArray(activeResume?.skills) ? activeResume.skills : [];
  const categories = normaliseCategories(rawSkills, activeResume?.skillCategories);

  // ── CATEGORY MODE helpers ──────────────────────────────────────────────────

  const persistCategories = (nextCats) => {
    const flatSkills = nextCats.flatMap(c => c.skills);
    const catsToStore = nextCats.map(({ id, name, skills }) => ({ id, name, skills }));
    updateResumeState({
      ...JSON.parse(JSON.stringify(activeResume || {})),
      skillCategories: catsToStore,
      skills: flatSkills,
    });
  };

  const handleAddCategory = (name) => {
    const next = [...categories, { id: uid(), name, skills: [] }];
    persistCategories(next);
    setShowAddPanel(false);
  };

  const handleRenameCategory = (catId, newName) => {
    persistCategories(categories.map(c => c.id === catId ? { ...c, name: newName } : c));
  };

  const handleDeleteCategory = (catId) => {
    const target = categories.find(c => c.id === catId);
    if (target && !window.confirm(`Delete category "${target.name}"? All skills inside will be removed.`)) return;
    const next = categories.filter(c => c.id !== catId);
    if (next.length === 0) {
      // Last category deleted → revert to flat mode
      updateResumeState({
        ...JSON.parse(JSON.stringify(activeResume || {})),
        skillCategories: [],
        skills: rawSkills, // restore original flat skills
      });
    } else {
      persistCategories(next);
    }
  };

  const handleAddSkillToCategory = (catId, skill) => {
    persistCategories(categories.map(c => {
      if (c.id !== catId || c.skills.includes(skill)) return c;
      return { ...c, skills: [...c.skills, skill] };
    }));
  };

  const handleDeleteSkillFromCategory = (catId, skill) => {
    persistCategories(categories.map(c => {
      if (c.id !== catId) return c;
      return { ...c, skills: c.skills.filter(s => s !== skill) };
    }));
  };

  // ── Enable categories: migrate flat skills to first category ──────────────
  const handleEnableCategories = (firstCategoryName) => {
    // Move all existing flat skills into the first user-named category
    const firstCat = { id: uid(), name: firstCategoryName, skills: rawSkills };
    persistCategories([firstCat]);
    setShowAddPanel(false);
  };

  // ── FLAT MODE helpers ──────────────────────────────────────────────────────
  const persistFlat = (nextSkills) => {
    updateResumeState({
      ...JSON.parse(JSON.stringify(activeResume || {})),
      skillCategories: [],
      skills: nextSkills,
    });
  };

  const commitFlatAdd = () => {
    const trimmed = flatInput.trim().replace(/,+$/, '');
    if (trimmed && !rawSkills.includes(trimmed)) {
      persistFlat([...rawSkills, trimmed]);
    }
    setFlatInput('');
  };

  const handleFlatKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitFlatAdd();
    }
  };

  const handleDeleteFlatSkill = (skill) => {
    persistFlat(rawSkills.filter(s => s !== skill));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CATEGORY MODE
  // ─────────────────────────────────────────────────────────────────────────
  if (categories !== null) {
    return (
      <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
          Organise your skills into categories. Each category appears as a labelled row in your resume.
        </p>

        {categories.length === 0 && (
          <div style={{
            padding: '20px', textAlign: 'center',
            border: '1.5px dashed var(--card-border)', borderRadius: '10px',
            color: 'var(--text-muted)', fontSize: '12px',
          }}>
            No skill categories yet — add your first one below.
          </div>
        )}

        {categories.map(cat => (
          <CategoryCard
            key={cat.id}
            category={cat}
            onRename={handleRenameCategory}
            onDelete={handleDeleteCategory}
            onAddSkill={handleAddSkillToCategory}
            onDeleteSkill={handleDeleteSkillFromCategory}
          />
        ))}

        {showAddPanel ? (
          <AddCategoryPanel
            existing={categories}
            onAdd={handleAddCategory}
            onClose={() => setShowAddPanel(false)}
          />
        ) : (
          <button
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', background: 'var(--surface-1)',
              border: '1.5px dashed var(--card-border)', borderRadius: '8px',
              color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600,
              cursor: 'pointer', width: '100%', justifyContent: 'center',
              transition: 'border-color 0.2s, color 0.2s, background 0.2s',
            }}
            onClick={() => setShowAddPanel(true)}
          >
            <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
            Add Skill Category
          </button>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FLAT MODE — no categories set up
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
        Add skills below, or organise them into categories (Languages, Backend, Frontend, etc.)
      </p>

      {/* Flat skill input */}
      <input
        type="text"
        value={flatInput}
        onChange={e => setFlatInput(e.target.value)}
        onKeyDown={handleFlatKeyDown}
        onBlur={() => { if (flatInput.trim()) commitFlatAdd(); }}
        placeholder="Add a skill… (Enter or comma to add)"
        style={{
          width: '100%', padding: '9px 12px', background: 'var(--surface-1)',
          border: '1px solid var(--card-border)', borderRadius: '8px',
          color: 'var(--text-primary)', fontSize: '13px', outline: 'none',
          transition: 'border-color 0.15s ease', boxSizing: 'border-box',
        }}
      />

      {/* Flat skill tags */}
      {rawSkills.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {rawSkills.map(skill => (
            <div
              key={skill}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                padding: '3px 10px', background: 'var(--brand-light)',
                border: '1px solid var(--brand-border)', borderRadius: '20px',
                color: 'var(--brand)', fontSize: '12px', fontWeight: 600,
              }}
            >
              <span>{skill}</span>
              <button
                onClick={() => handleDeleteFlatSkill(skill)}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--text-muted)',
                  cursor: 'pointer', padding: '0 2px', fontSize: '11px',
                  lineHeight: 1, display: 'flex', alignItems: 'center',
                }}
                title="Remove skill"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Enable category management */}
      <div style={{
        padding: '12px 14px', background: 'var(--surface-1)',
        border: '1.5px dashed var(--card-border)', borderRadius: '10px',
      }}>
        <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          💡 Want to organise by category (e.g. Languages, Backend, Frontend)?
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {SUGGESTED_CATEGORIES.map(name => (
            <button
              key={name}
              onClick={() => handleEnableCategories(name)}
              style={{
                padding: '4px 10px', fontSize: '11px', fontWeight: 600,
                background: 'var(--surface-0)', border: '1px solid var(--card-border)',
                borderRadius: '14px', cursor: 'pointer', color: 'var(--text-secondary)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              + {name}
            </button>
          ))}
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--text-muted)' }}>
          Click any category above to migrate your {rawSkills.length} skill{rawSkills.length !== 1 ? 's' : ''} into it and start categorising.
        </p>
      </div>
    </div>
  );
}
